import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  AddOutlined,
  CalendarTodayOutlined,
  DeleteOutline,
  ListAltOutlined,
  RefreshOutlined,
  ScheduleOutlined,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/Api';
import {
  getApiErrorMessage,
  getClassDisplayName,
  getStoredTeacher,
  teacherPortalGridItemSx,
  teacherPortalPanelCardSx,
  teacherPortalRowSx,
} from './teacherPortalUtils';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

export default function TeacherTimetableScreen() {
  const theme = useTheme();
  const teacher = getStoredTeacher();
  const classes = Array.isArray(teacher.assignedClasses) ? teacher.assignedClasses : [];
  const [selectedClassId, setSelectedClassId] = useState(classes[0]?._id || '');
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState({
    subject: '',
    day: 'Monday',
    startTime: '08:00',
    endTime: '09:00',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const groupedEntries = useMemo(() => {
    const grouped = {};
    DAYS.forEach((day) => {
      grouped[day] = [];
    });
    entries.forEach((entry) => {
      const day = entry.day || 'Monday';
      if (!grouped[day]) grouped[day] = [];
      grouped[day].push(entry);
    });
    Object.keys(grouped).forEach((day) => {
      grouped[day].sort((a, b) => String(a.startTime || '').localeCompare(String(b.startTime || '')));
    });
    return grouped;
  }, [entries]);

  const loadEntries = useCallback(async () => {
    if (!selectedClassId) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/timetable/teacher/class', {
        params: { classId: selectedClassId },
      });
      const data = res.data?.timetable || res.data?.entries || res.data?.data || res.data || [];
      setEntries(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load timetable.'));
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [selectedClassId]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!selectedClassId || !form.subject.trim()) return;
    setSaving(true);
    setError('');
    try {
      await api.post('/timetable/class-teacher', {
        classId: selectedClassId,
        subject: form.subject.trim(),
        day: form.day,
        startTime: form.startTime,
        endTime: form.endTime,
      });
      toast.success('Timetable period added.');
      setForm((prev) => ({ ...prev, subject: '' }));
      loadEntries();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to add timetable period.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/timetable/${id}`);
      toast.success('Timetable period deleted.');
      setEntries((prev) => prev.filter((entry) => (entry._id || entry.id) !== id));
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to delete timetable period.'));
    }
  };

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="h4" fontWeight={900}>Timetable</Typography>
          <Typography color="text.secondary">View and manage class timetable periods.</Typography>
        </Box>
        <Button startIcon={<RefreshOutlined />} onClick={loadEntries} disabled={loading} variant="outlined" sx={{ textTransform: 'none' }}>
          Refresh
        </Button>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} md={4} sx={teacherPortalGridItemSx}>
          <Card sx={teacherPortalPanelCardSx(theme)}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <ScheduleOutlined fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={800}>
                    Add Period
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Add a class timetable period.
                  </Typography>
                </Box>
              </Stack>
              <Stack component="form" onSubmit={handleCreate} spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Class</InputLabel>
                  <Select label="Class" value={selectedClassId} onChange={(event) => setSelectedClassId(event.target.value)}>
                    {classes.map((cls) => (
                      <MenuItem key={cls._id} value={cls._id}>{getClassDisplayName(cls)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Day</InputLabel>
                  <Select label="Day" value={form.day} onChange={(event) => setForm((prev) => ({ ...prev, day: event.target.value }))}>
                    {DAYS.map((day) => <MenuItem key={day} value={day}>{day}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField label="Subject" value={form.subject} onChange={(event) => setForm((prev) => ({ ...prev, subject: event.target.value }))} fullWidth />
                <Stack direction="row" spacing={1}>
                  <TextField label="Start" type="time" value={form.startTime} onChange={(event) => setForm((prev) => ({ ...prev, startTime: event.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
                  <TextField label="End" type="time" value={form.endTime} onChange={(event) => setForm((prev) => ({ ...prev, endTime: event.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
                </Stack>
                <Button type="submit" variant="contained" startIcon={<AddOutlined />} disabled={saving} sx={{ textTransform: 'none', borderRadius: 1 }}>
                  Add Period
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8} sx={teacherPortalGridItemSx}>
          <Card sx={teacherPortalPanelCardSx(theme)}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: { xs: 2, md: 3 }, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: 'info.main' }}>
                    <ListAltOutlined fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={800}>
                      Timetable Schedule ({entries.length})
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Review periods grouped by day.
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Stack spacing={1.5}>
                  {DAYS.map((day) => (
                    <Box key={day} sx={teacherPortalRowSx(theme)}>
                      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                          <CalendarTodayOutlined fontSize="small" />
                        </Avatar>
                        <Typography variant="h6" fontWeight={800}>
                          {day}
                        </Typography>
                      </Stack>
                      <Stack spacing={1}>
                        {(groupedEntries[day] || []).map((entry) => {
                          const id = entry._id || entry.id;
                          return (
                            <Box
                              key={id}
                              sx={teacherPortalRowSx(theme, {
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 2,
                                flexWrap: 'wrap',
                              })}
                            >
                              <Box>
                                <Typography fontWeight={800}>{entry.subject}</Typography>
                                <Typography variant="body2" color="text.secondary">{entry.startTime} - {entry.endTime}</Typography>
                              </Box>
                              <Button color="error" startIcon={<DeleteOutline />} onClick={() => handleDelete(id)} sx={{ textTransform: 'none' }}>
                                Delete
                              </Button>
                            </Box>
                          );
                        })}
                        {(groupedEntries[day] || []).length === 0 && (
                          <Typography color="text.secondary">No periods.</Typography>
                        )}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
}
